/** The full path to a node in the tree */
export class Branch {
    constructor(public readonly values: string[] = []) { }

    static fromValues(values: Array<string | number | null | undefined> | null | undefined): Branch {
        // Build a contiguous branch from root and stop at the first blank value.
        // This prevents deeper stale values from being treated as a valid path when an ancestor is unset.
        const selected: string[] = [];
        for (const selectedValue of values || []) {
            if (selectedValue === null || selectedValue === undefined || selectedValue === "") {
                break;
            }

            selected.push(String(selectedValue));
        }

        return new Branch(selected);
    }

    /** Returns the 1-based depth of this branch within the tree. */
    get level(): number {
        return this.values.length;
    }

    /** Checks if the branch is empty. */
    get empty(): boolean {
        return this.values.length === 0;
    }

    /** Creates a new branch with the specified value appended onto it */
    with(value: string): Branch {
        return new Branch([...this.values, value]);
    }

    /** Returns the value of the terminal node, or null if the branch is empty. */
    get terminalValue(): string | null {
        return this.values.at(-1) || null;
    }

    /**
     * Returns true when this branch is an immediate child of the specified parent branch.
     * Unlike a hypothetical descendant check, this only matches one level deeper.
     */
    isChildOf(parent: Branch): boolean {
        if (this.values.length !== parent.values.length + 1) {
            return false;
        }

        return new Branch(this.values.slice(0, -1)).equals(parent);
    }

    equals(other: Branch): boolean {
        if (this.values.length !== other.values.length) {
            return false;
        }

        return this.values.every((value, index) => {
            return value === other.values[index];
        });
    }
}

